import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  VideoLibrary as VideoIcon,
  Assignment as AssignmentIcon,
  TipsAndUpdates as TipsIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { resolveCurriculumChain, useCurriculumAIPolling } from '../hooks/useDeliverablesPolling.ts';
import type { TeacherNotesAndPlan } from '../types/curriculum.types.ts';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useRecoilValue } from 'recoil';
import { curriculumSelectionAtom } from './Curriculumselectionatom.ts';
import BoardClassSubjectSelector from './Boardclasssubjectselector.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Subtopic {
  subtopic: string;
  marks: number;
  prerequisites?: { name: string }[];
}

interface Topic {
  topic: string;
  description?: string;
  subtopics: Subtopic[];
  learning_material?: string[];
  topic_prerequisites?: { name: string }[];
}

interface KnowledgeMap {
  id?: string;
  _id?: string;
  board: string;
  class: string;
  subject: string;
  topics: Topic[];
  books?: string[];
}

interface WeeklyGuidelines {
  focus: string;
  videos: { name: string; duration: string; link: string }[];
  problems: { name: string; difficulty: string; problems: number }[];
  teachingTechniques: string[];
  demoExamples: string[];
  timeAllocation: {
    explanation: string;
    examples: string;
    practice: string;
    assessment: string;
  };
}

interface ClassGuidelines {
  [key: string]: WeeklyGuidelines;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

function generateGuidelinesFromTopics(topics: Topic[], classType: string, className: string): ClassGuidelines {
  if (!topics || topics.length === 0) return {};

  const guidelines: ClassGuidelines = {};
  const difficulty = classType === 'smart' ? 'Hard' : 'Medium';
  const videoDuration = classType === 'smart' ? '15-20 min' : '10-15 min';
  const problemsCount = classType === 'smart' ? 12 : 8;

  topics.forEach((topic, idx) => {
    const weekNum = idx + 1;
    const weekKey = `week${weekNum}`;

    guidelines[weekKey] = {
      focus: topic.topic,
      videos: [
        { name: `Introduction to ${topic.topic}`, duration: videoDuration, link: '#' },
        { name: `${topic.topic} - Deep Dive`, duration: videoDuration, link: '#' },
        ...(topic.learning_material && topic.learning_material.length > 0
          ? topic.learning_material.slice(0, 2).map(m => ({ name: m, duration: videoDuration, link: '#' }))
          : []
        ),
      ],
      problems: [
        { name: `${topic.topic} Worksheet`, difficulty: difficulty === 'Hard' ? 'Hard' : 'Medium', problems: problemsCount },
        { name: `${topic.topic} Practice Problems`, difficulty: difficulty === 'Hard' ? 'Expert' : 'Medium', problems: problemsCount - 2 },
        ...(topic.subtopics && topic.subtopics.length > 0
          ? [{ name: `${topic.subtopics[0].subtopic} Focus`, difficulty: difficulty, problems: Math.floor(problemsCount / 2) }]
          : []
        ),
      ],
      teachingTechniques: [
        `Interactive discussion on ${topic.topic}`,
        `Group activity for ${topic.subtopics?.[0]?.subtopic || topic.topic}`,
        `Real-world examples and applications`,
        classType === 'smart' ? 'Peer teaching and presentations' : 'Step-by-step problem solving',
        `Quick assessment and feedback loop`,
      ],
      demoExamples: [
        `Example 1: Core concept of ${topic.topic}`,
        `Example 2: Practical application of ${topic.topic}`,
        ...(topic.subtopics && topic.subtopics.length > 0
          ? [`Example 3: ${topic.subtopics[0].subtopic} in detail`]
          : []
        ),
      ],
      timeAllocation: {
        explanation: classType === 'smart' ? '15 min' : '20 min',
        examples: classType === 'smart' ? '25 min' : '20 min',
        practice: classType === 'smart' ? '20 min' : '15 min',
        assessment: '10 min',
      },
    };
  });

  return guidelines;
}

function mapTeacherNotesToGuidelines(
  notes: TeacherNotesAndPlan,
  classType: string,
  className: string
): ClassGuidelines {
  if (!notes.content?.length) return {};

  const guidelines: ClassGuidelines = {};
  notes.content.forEach((item, idx) => {
    const weekKey = `week${idx + 1}`;
    const noteLines = (item.notes ?? '').split('\n').filter(Boolean);
    guidelines[weekKey] = {
      focus: item.topic,
      videos: [{ name: `${item.topic} Overview`, duration: '15 min', link: '#' }],
      problems: [{ name: `${item.topic} Practice`, difficulty: classType === 'smart' ? 'Hard' : 'Medium', problems: 8 }],
      teachingTechniques: noteLines.length > 0 ? noteLines : [`Teach ${item.topic} with examples`],
      demoExamples: noteLines.slice(0, 3).length > 0 ? noteLines.slice(0, 3) : [`Demo: ${item.topic}`],
      timeAllocation: {
        explanation: classType === 'smart' ? '15 min' : '20 min',
        examples: classType === 'smart' ? '25 min' : '20 min',
        practice: classType === 'smart' ? '20 min' : '15 min',
        assessment: '10 min',
      },
    };
  });
  return guidelines;
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface TeacherGuidelinesProps {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
  onProceedToClassroom?: (board: string, classVal: string, subject: string) => void;
  onNavigateToStudyPlan?: (board: string, classVal: string, subject: string) => void;
  onNavigateToSyllabus?: (board: string, classVal: string, subject: string) => void;
}

export default function TeacherGuidelines({
  initialBoard,
  initialClass,
  initialSubject,
  onProceedToClassroom,
  onNavigateToStudyPlan,
  onNavigateToSyllabus,
}: TeacherGuidelinesProps) {
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();

  // State for API data
  const [knowledgeMaps, setKnowledgeMaps] = useState<KnowledgeMap[]>([]);
  const [loading, setLoading] = useState(false);

  // Selection state
  // const [selectedBoard, setSelectedBoard] = useState<string>(initialBoard ?? '');
  // const [selectedClass, setSelectedClass] = useState<string>(initialClass ?? '');
  // const [selectedSubject, setSelectedSubject] = useState<string>(initialSubject ?? '');

  // // Derived data
  // const [availableBoards, setAvailableBoards] = useState<string[]>([]);
  // const [availableClasses, setAvailableClasses] = useState<string[]>([]);
  // const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);
  const { board: selectedBoard, classVal: selectedClass, subject: selectedSubject } = useRecoilValue(curriculumSelectionAtom);

  const [currentKnowledgeMap, setCurrentKnowledgeMap] = useState<KnowledgeMap | null>(null);

  // UI state
  const [classType, setClassType] = useState<'regular' | 'smart'>('regular');
  const [expandedWeek, setExpandedWeek] = useState<string | null>('week1');
  const [guidelines, setGuidelines] = useState<ClassGuidelines>({});
  const [usingApiNotes, setUsingApiNotes] = useState(false);
  const [studyPlanId, setStudyPlanId] = useState<string | null>(null);
  const [hasSyllabus, setHasSyllabus] = useState(false);
  const [hasStudyPlan, setHasStudyPlan] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [notesPollEnabled, setNotesPollEnabled] = useState(false);

  const { status: notesPollStatus, progress: notesPollProgress } = useCurriculumAIPolling(
    studyPlanId,
    null,
    'teacher_notes',
    notesPollEnabled
  );

  // useEffect(() => {
  //   if (initialBoard) setSelectedBoard(initialBoard);
  //   if (initialClass) setSelectedClass(initialClass);
  //   if (initialSubject) setSelectedSubject(initialSubject);
  // }, [initialBoard, initialClass, initialSubject]);

  const loadCurriculumChain = async (found: KnowledgeMap) => {
    try {
      setLoading(true);
      const kmId = found.id ?? found._id ?? '';
      const chain = await resolveCurriculumChain(kmId, aiActions);

      setHasSyllabus(!!chain.syllabus);
      setHasStudyPlan(!!chain.studyPlan);
      setStudyPlanId(chain.studyPlan?._id ?? null);

      if (chain.teacherNotes?.content?.length) {
        setGuidelines(mapTeacherNotesToGuidelines(chain.teacherNotes, classType, selectedClass));
        setUsingApiNotes(true);
      } else if (found.topics?.length) {
        setGuidelines(generateGuidelinesFromTopics(found.topics, classType, selectedClass));
        setUsingApiNotes(false);
      } else {
        setGuidelines({});
        setUsingApiNotes(false);
      }
      setExpandedWeek('week1');
    } catch {
      if (found.topics?.length) {
        setGuidelines(generateGuidelinesFromTopics(found.topics, classType, selectedClass));
      } else {
        setGuidelines({});
      }
      setUsingApiNotes(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!currentKnowledgeMap) {
      await fetchKnowledgeMaps();
      return;
    }
    await loadCurriculumChain(currentKnowledgeMap);
  };

  const handleGenerateGuidelinesWithAI = async () => {
    if (!studyPlanId) {
      showSnackBar('Save a study plan first before generating guidelines.', 'warning');
      return;
    }
    setGenerating(true);
    try {
      await aiActions.generateTeacherGuidelinesWithAI(studyPlanId);
      setNotesPollEnabled(true);
      showSnackBar('AI teacher guidelines generation started...', 'info');
    } catch (error) {
      console.error('Error triggering AI guidelines:', error);
      showSnackBar('Failed to start AI guidelines generation.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (notesPollStatus === 'complete' && currentKnowledgeMap) {
      setNotesPollEnabled(false);
      showSnackBar('AI teacher guidelines generated!', 'success');
      loadCurriculumChain(currentKnowledgeMap);
    } else if (notesPollStatus === 'timeout') {
      showSnackBar('Guidelines generation is taking longer than expected.', 'warning');
      setNotesPollEnabled(false);
    }
  }, [notesPollStatus, currentKnowledgeMap, showSnackBar]);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Teacher Guidelines' },
    ...(selectedBoard ? [{ label: selectedBoard }] : []),
    ...(selectedClass ? [{ label: `Class ${selectedClass}` }] : []),
    ...(selectedSubject ? [{ label: selectedSubject }] : []),
  ];

  // Fetch all knowledge maps on component mount
  useEffect(() => {
    fetchKnowledgeMaps();
  }, []);

  const fetchKnowledgeMaps = async () => {
    try {
      setLoading(true);
      const maps = await aiActions.getKnowledgeList();
      setKnowledgeMaps(maps || []);

      // const boards = [...new Set(maps.map((map: KnowledgeMap) => map.board).filter(Boolean))].sort();
      // setAvailableBoards(boards);
    } catch (error) {
      console.error('Error fetching knowledge maps:', error);
      showSnackBar('Failed to load knowledge maps', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update available classes when board changes
  // useEffect(() => {
  //   if (selectedBoard) {
  //     const classes = [...new Set(
  //       knowledgeMaps.filter(km => km.board === selectedBoard).map(km => km.class)
  //     )].sort();
  //     setAvailableClasses(classes);
  //   } else {
  //     setAvailableClasses([]);
  //   }
  // }, [selectedBoard, knowledgeMaps]);

  // Update available subjects when class changes
  // useEffect(() => {
  //   if (selectedBoard && selectedClass) {
  //     const subjects = [...new Set(
  //       knowledgeMaps.filter(km => km.board === selectedBoard && km.class === selectedClass).map(km => km.subject)
  //     )].sort();
  //     setAvailableSubjects(subjects);
  //   } else {
  //     setAvailableSubjects([]);
  //   }
  // }, [selectedBoard, selectedClass, knowledgeMaps]);

  // Update current knowledge map and load guidelines when selection changes
  useEffect(() => {
    if (selectedBoard && selectedClass && selectedSubject) {
      const found = knowledgeMaps.find(
        (km) => km.board === selectedBoard && km.class === selectedClass && km.subject === selectedSubject
      );
      setCurrentKnowledgeMap(found || null);

      if (found) {
        loadCurriculumChain(found);
      } else {
        setGuidelines({});
        setUsingApiNotes(false);
        setHasSyllabus(false);
        setHasStudyPlan(false);
        setStudyPlanId(null);
      }
    } else {
      setCurrentKnowledgeMap(null);
      setGuidelines({});
      setUsingApiNotes(false);
      setHasSyllabus(false);
      setHasStudyPlan(false);
      setStudyPlanId(null);
    }
  }, [selectedBoard, selectedClass, selectedSubject, knowledgeMaps]);

  useEffect(() => {
    if (currentKnowledgeMap && !usingApiNotes) {
      loadCurriculumChain(currentKnowledgeMap);
    }
  }, [classType]);

  const weeks = Object.keys(guidelines);
  const totalTopics = currentKnowledgeMap?.topics.length || 0;
  const totalSubtopics = currentKnowledgeMap?.topics.reduce((sum, t) => sum + (t.subtopics?.length || 0), 0) || 0;
  const totalVideos = weeks.reduce((sum, w) => sum + (guidelines[w]?.videos.length || 0), 0);
  const totalProblems = weeks.reduce((sum, w) => sum + (guidelines[w]?.problems.reduce((s, p) => s + p.problems, 0) || 0), 0);

  // Dropdown options
  // const boardOptions = availableBoards.map(board => ({ label: board, value: board }));
  // const classOptions = availableClasses.map(cls => ({ label: `Class ${cls}`, value: cls }));
  // const subjectOptions = availableSubjects.map(sub => ({ label: sub, value: sub }));

  const hasValidSelection = selectedBoard && selectedClass && selectedSubject;
  const hasGuidelines = weeks.length > 0;

  return (
    <Box sx={{ p: 1.5, bgcolor: '#F5F7FA', minHeight: '100vh', paddingLeft:{md:'200px',sm:'100%'} }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <DynamicBreadcrumbs items={breadcrumbItems} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{ fontSize: '10px', minHeight: 28, py: 0 }}
        >
          Refresh
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <BoardClassSubjectSelector
          externalLoading={loading}
          colProps={{ xs: 12, sm: 3 }}
        />
        {/* Selection Panel */}
        <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          {/* <Grid container spacing={1}> */}
          {/* <Grid item xs={12} sm={3}>
            <SearchableDropdown
              options={boardOptions}
              labelKey="label"
              valueKey="value"
              label="Board"
              value={selectedBoard ? { label: selectedBoard, value: selectedBoard } : null}
              onChange={(val: any) => { 
                setSelectedBoard(val?.value || ''); 
                setSelectedClass(''); 
                setSelectedSubject('');
              }}
              loading={loading}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
            />
          </Grid> */}
          {/* <Grid item xs={12} sm={3}>
            <SearchableDropdown
              options={classOptions}
              labelKey="label"
              valueKey="value"
              label="Class"
              value={selectedClass ? { label: `Class ${selectedClass}`, value: selectedClass } : null}
              onChange={(val: any) => { 
                setSelectedClass(val?.value || ''); 
                setSelectedSubject('');
              }}
              loading={loading}
              disabled={!selectedBoard}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
            />
          </Grid> */}
          {/* <Grid item xs={12} sm={3}>
            <SearchableDropdown
              options={subjectOptions}
              labelKey="label"
              valueKey="value"
              label="Subject"
              value={selectedSubject ? { label: selectedSubject, value: selectedSubject } : null}
              onChange={(val: any) => setSelectedSubject(val?.value || '')}
              loading={loading}
              disabled={!selectedClass}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
            />
          </Grid> */}
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
              <Button
                variant={classType === 'regular' ? 'contained' : 'outlined'}
                onClick={() => setClassType('regular')}
                size="small"
                fullWidth
                sx={{ fontSize: '9px', minHeight: 36 }}
              >
                Regular Class
              </Button>
              <Button
                variant={classType === 'smart' ? 'contained' : 'outlined'}
                onClick={() => setClassType('smart')}
                size="small"
                fullWidth
                sx={{ fontSize: '9px', minHeight: 36 }}
              >
                Smart Class
              </Button>
            </Box>
          </Grid>
          {/* </Grid> */}
        </Paper>
      </Box>
      {/* No Selection Message */}
      {!loading && !hasValidSelection && (
        <Alert severity="info" sx={{ fontSize: '10px', py: 0, mb: 1.5, '& .MuiAlert-message': { fontSize: '10px' } }}>
          Select a Board, Class, and Subject to generate teacher guidelines.
        </Alert>
      )}

      {/* No Data Message */}
      {!loading && hasValidSelection && !currentKnowledgeMap && (
        <Alert severity="warning" sx={{ fontSize: '10px', py: 0, mb: 1.5, '& .MuiAlert-message': { fontSize: '10px' } }}>
          No knowledge map found for {selectedBoard} - Class {selectedClass} - {selectedSubject}
        </Alert>
      )}

      {/* Pipeline status banner */}
      {hasValidSelection && currentKnowledgeMap && (
        <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          {!hasSyllabus && onNavigateToSyllabus && (
            <Alert severity="warning" sx={{ mb: 1, fontSize: 10 }} action={
              <Button color="inherit" size="small" onClick={() => onNavigateToSyllabus(selectedBoard, selectedClass, selectedSubject)}>
                Go to Unit Syllabus
              </Button>
            }>
              No syllabus found. Create one in Unit Syllabus first.
            </Alert>
          )}
          {hasSyllabus && !hasStudyPlan && onNavigateToStudyPlan && (
            <Alert severity="warning" sx={{ mb: 1, fontSize: 10 }} action={
              <Button color="inherit" size="small" onClick={() => onNavigateToStudyPlan(selectedBoard, selectedClass, selectedSubject)}>
                Go to Study Plan
              </Button>
            }>
              No study plan found. Generate and save a study plan first.
            </Alert>
          )}
          {hasStudyPlan && !usingApiNotes && (
            <Alert severity="info" sx={{ mb: 1, fontSize: 10 }} action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={handleGenerateGuidelinesWithAI}
                disabled={generating || notesPollEnabled}
              >
                Generate Guidelines with AI
              </Button>
            }>
              Study plan ready. Generate AI teacher guidelines for this subject.
            </Alert>
          )}
          {usingApiNotes && (
            <Alert severity="success" sx={{ mb: 1, fontSize: 10 }}>
              Teacher guidelines loaded from AI-generated notes.
            </Alert>
          )}
          {notesPollEnabled && (
            <Alert severity="info" sx={{ fontSize: 10 }}>
              Generating AI teacher guidelines... {notesPollProgress}%
              <LinearProgress variant="determinate" value={notesPollProgress} sx={{ mt: 0.5 }} />
            </Alert>
          )}
          {usingApiNotes && onProceedToClassroom && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => onProceedToClassroom(selectedBoard, selectedClass, selectedSubject)}
              >
                Proceed to Classroom
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Info Alert */}
      {hasValidSelection && currentKnowledgeMap && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0, '& .MuiAlert-message': { fontSize: '10px' } }} icon={<TipsIcon sx={{ fontSize: 14 }} />}>
          Teacher guidelines generated from {totalTopics} topic(s) and {totalSubtopics} subtopic(s) for Class {selectedClass} ({classType === 'smart' ? 'Smart Class' : 'Regular Class'})
        </Alert>
      )}

      {/* Summary Stats */}
      {hasGuidelines && (
        <Grid container spacing={0.75} sx={{ mb: 1.5 }}>
          {[
            { label: 'Weeks Planned', value: weeks.length },
            { label: 'Topics', value: totalTopics },
            { label: 'Video Lessons', value: totalVideos },
            { label: 'Practice Problems', value: totalProblems },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Paper elevation={0} sx={{ p: 0.75, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#F8F9FB' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: 18 }}>{s.value}</Typography>
                <Typography variant="caption" sx={{ fontSize: 9 }}>{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Guidelines Accordions */}
      {hasGuidelines && (
        <>
          {weeks.map((week, idx) => (
            <Accordion
              key={week}
              expanded={expandedWeek === week}
              onChange={() => setExpandedWeek(expandedWeek === week ? null : week)}
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Week ${idx + 1}`}
                    color="primary"
                    size="small"
                    sx={{ height: 22, fontSize: 10 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 11 }}>
                    {guidelines[week]?.focus}
                  </Typography>
                  <Chip
                    label={`${guidelines[week]?.timeAllocation.explanation} + ${guidelines[week]?.timeAllocation.practice}`}
                    size="small"
                    sx={{ height: 20, fontSize: 8 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
                <Grid container spacing={1.5}>
                  {/* Videos Section */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: 10 }}>
                        <VideoIcon sx={{ fontSize: 14 }} /> Videos to Refer
                      </Typography>
                      {guidelines[week]?.videos.map((video, idx2) => (
                        <Box key={idx2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, p: 0.75, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontSize: 9 }}>{video.name}</Typography>
                          <Chip label={video.duration} size="small" sx={{ height: 18, fontSize: 8 }} />
                        </Box>
                      ))}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: 10 }}>
                        <AssignmentIcon sx={{ fontSize: 14 }} /> Problems to Solve
                      </Typography>
                      {guidelines[week]?.problems.map((problem, idx2) => (
                        <Box key={idx2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, p: 0.75, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontSize: 9 }}>{problem.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip
                              label={problem.difficulty}
                              size="small"
                              color={problem.difficulty === 'Easy' ? 'success' : problem.difficulty === 'Medium' ? 'warning' : 'error'}
                              sx={{ height: 18, fontSize: 8 }}
                            />
                            <Chip label={`${problem.problems} problems`} size="small" variant="outlined" sx={{ height: 18, fontSize: 8 }} />
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>

                  {/* Teaching Techniques Section */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: 10 }}>
                        <TipsIcon sx={{ fontSize: 14 }} /> Teaching Techniques
                      </Typography>
                      <List dense disablePadding>
                        {guidelines[week]?.teachingTechniques.map((tech, idx2) => (
                          <ListItem key={idx2} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <CheckIcon sx={{ fontSize: 12, color: '#4caf50' }} />
                            </ListItemIcon>
                            <ListItemText primary={tech} primaryTypographyProps={{ fontSize: 9 }} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: 10 }}>
                        <GroupIcon sx={{ fontSize: 14 }} /> Demo Examples
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {guidelines[week]?.demoExamples.map((example, idx2) => (
                          <Chip
                            key={idx2}
                            label={example}
                            size="small"
                            sx={{ height: 'auto', py: 0.5, fontSize: 9, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                          />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Time Allocation */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: 10 }}>
                        <ScheduleIcon sx={{ fontSize: 14 }} /> Time Allocation
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(guidelines[week]?.timeAllocation || {}).map(([key, value]) => (
                          <Grid item xs={3} key={key}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8, textTransform: 'uppercase' }}>{key}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 11 }}>{value}</Typography>
                            <LinearProgress
                              variant="determinate"
                              value={parseInt(value) * 2}
                              sx={{ height: 3, mt: 0.5, borderRadius: 2 }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      {/* Teacher Plan Summary Card */}
      {hasGuidelines && (
        <Paper elevation={0} sx={{ p: 1.5, mt: 1.5, borderRadius: 1.5, bgcolor: '#E8F5E9', border: '1px solid', borderColor: '#A5D6A7' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: 11 }}>📋 Complete Teacher Plan Summary</Typography>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <Typography variant="h6" color="primary" sx={{ fontSize: 16, fontWeight: 700 }}>{weeks.length}</Typography>
              <Typography variant="caption" sx={{ fontSize: 8 }}>Weeks Planned</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="h6" color="primary" sx={{ fontSize: 16, fontWeight: 700 }}>{totalVideos}</Typography>
              <Typography variant="caption" sx={{ fontSize: 8 }}>Video Lessons</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="h6" color="primary" sx={{ fontSize: 16, fontWeight: 700 }}>{totalProblems}+</Typography>
              <Typography variant="caption" sx={{ fontSize: 8 }}>Practice Problems</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="h6" color="primary" sx={{ fontSize: 16, fontWeight: 700 }}>{weeks.length}</Typography>
              <Typography variant="caption" sx={{ fontSize: 8 }}>Assessments</Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8 }}>
            💡 Week-wise guidance generated from knowledge map topics for {classType === 'smart' ? 'Smart Class' : 'Regular Class'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}